import React from 'react';
import { X, User, Mail, Phone, MapPin, Calendar, Mars, Venus, Transgender } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../CommonLoader';
import { getFileURL } from '../../utils/fileURL';
import MasterLabel from '../common/MasterLabel';

const PatientDetailsModal = ({ isOpen, onClose, patient, loading }) => {
    const { t } = useTranslation('patientdetailsmodal');

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return t('patientdetailsmodal.na');
        let date;
        const str = String(dateString).trim();
        if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
            date = new Date(str);
        } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
            const parts = str.split(/[/-]/);
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
            date = new Date(str);
        }
        if (isNaN(date.getTime())) return t('patientdetailsmodal.na');
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="patient-details-modal-overlay"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="patient-details-modal"
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white px-6 py-4 flex justify-between items-center shadow-md" data-testid="patient-details-modal-header">
                            <h2 className="text-xl sm:text-2xl font-bold" data-testid="patient-details-modal-title">
                                {t('patientdetailsmodal.title')}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                                aria-label={t('patientdetailsmodal.close')}
                                data-testid="patient-details-modal-close-button"
                            >
                                <X className="w-6 h-6" data-testid="patient-details-modal-close-icon" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 custom-scrollbar" data-testid="patient-details-modal-content">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="patient-details-modal-loading-section">
                                    <CommonLoader />
                                    <span className="text-xl text-[#4ABDBD] font-semibold mt-4" data-testid="patient-details-modal-loading-text">
                                        {t('patientdetailsmodal.loading')}
                                    </span>
                                </div>
                            ) : patient ? (
                                <div className="space-y-6" data-testid="patient-details-modal-data-section">
                                    {/* Personal Information Card */}
                                    <div className="bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-2xl p-6 shadow-sm" data-testid="patient-details-modal-personal-info-section">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-2 bg-[#4ABDBD] rounded-lg">
                                                <User className="text-white w-5 h-5" data-testid="patient-details-modal-personal-info-icon" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800" data-testid="patient-details-modal-personal-info-title">
                                                {t('patientdetailsmodal.personal_information')}
                                            </h3>
                                        </div>

                                        <div className="flex flex-col md:flex-row gap-6 items-start">
                                            {/* Avatar */}
                                            <div className="shrink-0" data-testid="patient-details-modal-avatar-container">
                                                {patient.image ? (
                                                    <img
                                                        src={getFileURL(patient.image)}
                                                        alt={`${patient.name} ${patient.surname}`}
                                                        className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                                                        data-testid="patient-details-modal-avatar-image"
                                                    />
                                                ) : (
                                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center border-4 border-white shadow-lg" data-testid="patient-details-modal-avatar-placeholder">
                                                        <User className="text-white w-16 h-16" data-testid="patient-details-modal-avatar-placeholder-icon" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Details Grid */}
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="patient-details-modal-details-grid">
                                                <div className="space-y-1" data-testid="patient-details-modal-full-name-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase" data-testid="patient-details-modal-full-name-label">
                                                        {t('patientdetailsmodal.full_name')}
                                                    </p>
                                                    <p className="text-base font-bold text-gray-900" data-testid="patient-details-modal-full-name-text">
                                                        {patient.name} {patient.surname}
                                                    </p>
                                                </div>

                                                <div className="space-y-1" data-testid="patient-details-modal-email-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-email-label">
                                                        <Mail className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-email-icon" />
                                                        {t('patientdetailsmodal.email')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900 break-all" data-testid="patient-details-modal-email-text">
                                                        {patient.email || t('patientdetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1" data-testid="patient-details-modal-phone-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-phone-label">
                                                        <Phone className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-phone-icon" />
                                                        {t('patientdetailsmodal.phone')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-phone-text">
                                                        {patient.phone || t('patientdetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1" data-testid="patient-details-modal-gender-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-gender-label">
                                                        {patient.gender?.toLowerCase() === 'male' && <Mars className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-gender-male-icon" />}
                                                        {patient.gender?.toLowerCase() === 'female' && <Venus className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-gender-female-icon" />}
                                                        {patient.gender?.toLowerCase() === 'other' && <Transgender className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-gender-other-icon" />}
                                                        {t('patientdetailsmodal.gender')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900 capitalize" data-testid="patient-details-modal-gender-text">
                                                        {patient.gender ? t(`common.gender.${patient.gender.toLowerCase()}`, patient.gender) : t('patientdetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1" data-testid="patient-details-modal-dob-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-dob-label">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-dob-icon" />
                                                        {t('patientdetailsmodal.date_of_birth')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-dob-text">
                                                        {patient.dob ? formatDate(patient.dob) : t('patientdetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1" data-testid="patient-details-modal-country-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-country-label">
                                                        <MapPin className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-country-icon" />
                                                        {t('patientdetailsmodal.country')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-country-text">
                                                        <MasterLabel type="masterCountry" id={patient.country?.countryId} field="countryName" fallback={patient.country?.countryName || patient.country || t('patientdetailsmodal.na')} />
                                                    </p>
                                                </div>

                                                {patient.city && (
                                                    <div className="space-y-1" data-testid="patient-details-modal-city-field">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-city-label">
                                                            <MapPin className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-city-icon" />
                                                            {t('patientdetailsmodal.city')}
                                                        </p>
                                                        <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-city-text">
                                                            {patient.city}
                                                        </p>
                                                    </div>
                                                )}

                                                {patient.zipCode && (
                                                    <div className="space-y-1" data-testid="patient-details-modal-zipcode-field">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase" data-testid="patient-details-modal-zipcode-label">
                                                            {t('patientdetailsmodal.zip_code')}
                                                        </p>
                                                        <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-zipcode-text">
                                                            {patient.zipCode}
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="space-y-1" data-testid="patient-details-modal-created-at-field">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1" data-testid="patient-details-modal-created-at-label">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" data-testid="patient-details-modal-created-at-icon" />
                                                        {t('patientdetailsmodal.created_at')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900" data-testid="patient-details-modal-created-at-text">
                                                        {patient.createdAt ? formatDate(patient.createdAt) : t('patientdetailsmodal.na')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="patient-details-modal-no-data-section">
                                    <p className="text-gray-500 text-lg" data-testid="patient-details-modal-no-data-text">
                                        {t('patientdetailsmodal.no_data')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PatientDetailsModal;

